import { useState, useEffect } from 'react';
import { X, Loader2, Archive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useRock, useUpdateRock, useArchiveRock, useCreateRock, useOwnerOptions } from '../hooks/useRocks';
import { useTeams } from '../hooks/useTeams';
import { RockStatusBadge } from './RockStatusBadge';
import { DeliverableList } from './DeliverableList';
import { CommentThread } from './CommentThread';
import { LinkedItemsList } from './LinkedItemsList';
import { LinkedTodoCreator } from '@/features/os-todos';
import { AttachmentsList } from '@/features/attachments';
import { BOARD_COLUMNS, ROCK_STATUSES } from '../types';
import { getCurrentQuarter, getQuarterEndDate, QUARTER_OPTIONS } from '../utils/quarter';
import type { RockStatus, RockType, BoardColumn } from '../types';

interface RockDetailPanelProps {
  rockId: string | null;
  mode: 'view' | 'create';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after successful creation with the new rock id */
  onCreated?: (id: string) => void;
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground uppercase tracking-wide">{label}</Label>
      {children}
    </div>
  );
}

function formatCreatedDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function RockDetailPanel({ rockId, mode, open, onOpenChange, onCreated }: RockDetailPanelProps) {
  const isCreate = mode === 'create';
  const { data: rock, isLoading } = useRock(rockId ?? '');
  const updateRock = useUpdateRock();
  const archiveRock = useArchiveRock();
  const createRock = useCreateRock();
  const { data: teams = [] } = useTeams();
  const { data: owners = [] } = useOwnerOptions();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [type, setType] = useState<RockType>('individual');
  const [status, setStatus] = useState<RockStatus>('draft');
  const [quarter, setQuarter] = useState(getCurrentQuarter());
  const [dueDate, setDueDate] = useState(getQuarterEndDate());
  const [boardColumn, setBoardColumn] = useState<BoardColumn>('current');

  // Reset form for create mode
  useEffect(() => {
    if (isCreate && open) {
      setTitle('');
      setDescription('');
      setOwnerId('');
      setTeamIds([]);
      setType('individual');
      setStatus('draft');
      const q = getCurrentQuarter();
      setQuarter(q);
      setDueDate(getQuarterEndDate(q));
      setBoardColumn('current');
    }
  }, [isCreate, open]);

  // Sync state from server data in view mode.
  // Only runs when the selected rock changes (not on every refetch),
  // so auto-save refetches don't wipe in-progress edits.
  useEffect(() => {
    if (!isCreate && rock) {
      setTitle(rock.title);
      setDescription(rock.description ?? '');
      setOwnerId(rock.ownerId);
      setTeamIds(
        Array.isArray(rock.teamIds) && rock.teamIds.length > 0
          ? rock.teamIds
          : rock.teamId ? [rock.teamId] : [],
      );
      setType(rock.type);
      setStatus(rock.status);
      setQuarter(rock.quarter);
      setDueDate(rock.dueDate ? rock.dueDate.slice(0, 10) : '');
      setBoardColumn(rock.boardColumn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rock?.id, isCreate]);

  const save = (data: Parameters<typeof updateRock.mutate>[0]['data']) => {
    if (!rockId || isCreate) return;
    updateRock.mutate({ id: rockId, data });
  };

  const handleQuarterChange = (v: string) => {
    setQuarter(v);
    if (isCreate) {
      setDueDate(getQuarterEndDate(v));
    } else {
      save({ quarter: v });
    }
  };

  const handleCreate = async () => {
    if (!title.trim() || !ownerId || teamIds.length === 0 || !quarter || !dueDate) return;
    try {
      const created = await createRock.mutateAsync({
        title: title.trim(),
        description: description.trim() || undefined,
        type,
        owner_id: ownerId,
        team_id: teamIds[0],
        team_ids: teamIds,
        quarter,
        due_date: dueDate,
        board_column: boardColumn,
        status,
      });
      onCreated?.(created.id);
    } catch {
      // Error toast handled by mutation onError
    }
  };

  const toggleTeam = (teamId: string) => {
    const next = teamIds.includes(teamId)
      ? teamIds.filter((id) => id !== teamId)
      : [...teamIds, teamId];
    if (next.length === 0) return; // at least one team required
    setTeamIds(next);
    if (!isCreate) save({ team_id: next[0], team_ids: next });
  };

  const handleArchive = () => {
    if (!rockId) return;
    archiveRock.mutate(rockId);
    onOpenChange(false);
  };

  if (!open) return null;

  const showLoading = !isCreate && isLoading;
  const showNotFound = !isCreate && !isLoading && !rock;
  const showForm = isCreate || (!isLoading && !!rock);

  const canCreate = isCreate && title.trim() && ownerId && teamIds.length > 0 && quarter && dueDate;

  return (
    <div className="h-full flex flex-col border-l bg-background overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          {!isCreate && rock && <RockStatusBadge status={rock.status} />}
          <span className="text-sm font-semibold truncate">
            {isCreate ? 'New Rock' : 'Rock Detail'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {!isCreate && rockId && (
            <Button variant="ghost" size="icon" className="h-7 w-7" title="Archive" onClick={handleArchive}>
              <Archive className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {showLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {showNotFound && (
          <p className="text-sm text-muted-foreground py-8 text-center">Rock not found.</p>
        )}

        {showForm && (
          <>
            {/* Title */}
            <Section label="Title *">
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={() => !isCreate && rock && title !== rock.title && save({ title })}
                placeholder="What needs to get done this quarter?"
                className="text-sm"
                autoFocus={isCreate}
              />
            </Section>

            {/* Owner & Type */}
            <div className="grid grid-cols-2 gap-3">
              <Section label="Owner *">
                <Select
                  value={ownerId}
                  onValueChange={(v) => { setOwnerId(v); if (!isCreate) save({ owner_id: v }); }}
                >
                  <SelectTrigger className="text-sm">
                    <SelectValue placeholder="Assign to..." />
                  </SelectTrigger>
                  <SelectContent>
                    {owners.map((owner) => (
                      <SelectItem key={owner.id} value={owner.id}>
                        {owner.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Section>

              <Section label="Type">
                <Select
                  value={type}
                  onValueChange={(v) => { setType(v as RockType); if (!isCreate) save({ type: v as RockType }); }}
                >
                  <SelectTrigger className="text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="company">Company Rock</SelectItem>
                    <SelectItem value="individual">Individual</SelectItem>
                  </SelectContent>
                </Select>
              </Section>
            </div>

            {/* Status & Board Column */}
            <div className="grid grid-cols-2 gap-3">
              <Section label="Status">
                <Select
                  value={status}
                  onValueChange={(v) => { setStatus(v as RockStatus); if (!isCreate) save({ status: v as RockStatus }); }}
                >
                  <SelectTrigger className="text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROCK_STATUSES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Section>

              <Section label="Board Column">
                <Select
                  value={boardColumn}
                  onValueChange={(v) => { setBoardColumn(v as BoardColumn); if (!isCreate) save({ board_column: v as BoardColumn }); }}
                >
                  <SelectTrigger className="text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BOARD_COLUMNS.map((col) => (
                      <SelectItem key={col.value} value={col.value}>
                        {col.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Section>
            </div>

            {/* Teams */}
            <Section label="Teams *">
              <div className="space-y-2 max-h-32 overflow-y-auto rounded-md border p-3">
                {teams.map((team) => (
                  <div key={team.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`rock-team-${team.id}`}
                      checked={teamIds.includes(team.id)}
                      onCheckedChange={() => toggleTeam(team.id)}
                    />
                    <Label htmlFor={`rock-team-${team.id}`} className="font-normal text-sm">
                      {team.name}
                    </Label>
                  </div>
                ))}
              </div>
            </Section>

            {/* Quarter & Due Date */}
            <div className="grid grid-cols-2 gap-3">
              <Section label="Quarter *">
                <Select value={quarter} onValueChange={handleQuarterChange}>
                  <SelectTrigger className="text-sm">
                    <SelectValue placeholder="Select quarter..." />
                  </SelectTrigger>
                  <SelectContent>
                    {QUARTER_OPTIONS.map((q) => (
                      <SelectItem key={q.value} value={q.value}>
                        {q.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Section>

              <Section label="Due Date *">
                <Input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  onBlur={() => !isCreate && rock && dueDate !== rock.dueDate?.slice(0, 10) && save({ due_date: dueDate })}
                  className="text-sm"
                />
              </Section>
            </div>

            {/* Description */}
            <Section label="Description">
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onBlur={() => !isCreate && rock && description !== (rock.description ?? '') && save({ description })}
                placeholder="Add details about this rock..."
                className="text-sm min-h-[200px]"
                rows={8}
              />
            </Section>

            {/* Sections only visible in edit mode */}
            {!isCreate && rock && (
              <>
                <Section label="Deliverables">
                  <DeliverableList rockId={rock.id} deliverables={rock.deliverables ?? []} />
                </Section>

                <Section label="Linked Items">
                  <LinkedItemsList sourceType="rock" sourceId={rock.id} />
                  <LinkedTodoCreator
                    sourceType="rock"
                    sourceId={rock.id}
                    teamId={rock.teamId ?? null}
                  />
                </Section>

                <Section label="Attachments">
                  <AttachmentsList entityType="rock" entityId={rock.id} />
                </Section>

                <Section label="Comments">
                  <CommentThread entityType="rock" entityId={rock.id} />
                </Section>
              </>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t shrink-0">
        {isCreate ? (
          <Button
            className="w-full"
            disabled={!canCreate || createRock.isPending}
            onClick={handleCreate}
          >
            {createRock.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create Rock
          </Button>
        ) : rock ? (
          <p className="text-xs text-muted-foreground">
            Created {formatCreatedDate(rock.createdAt)}
          </p>
        ) : null}
      </div>
    </div>
  );
}
